import ConfigProvider from 'antd/es/config-provider/index';
import { ANT_LOCALE_MAPPING } from 'common/constants/contstants';
import { getCSSNumberValue, getCSSVarValue } from 'common/utils/dom/getCSSVarValue';
import { AntApp } from 'components/ant/AntApp';
import { AntConfigProviderProps } from 'components/ant/AntConfigProvider';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import screenVariables from 'styles/utils/_variables.module.less';

import s from './AntThemeProvider.module.less';

const tooltip: NonNullable<AntConfigProviderProps['tooltip']> = {
  classNames: {
    root: s.tooltip,
  },
};

const select: NonNullable<AntConfigProviderProps['select']> = {
  classNames: {
    popup: {
      root: s.selectDropdown,
    },
  },
};

const modal: NonNullable<AntConfigProviderProps['modal']> = {
  classNames: {
    root: s.modalContent,
    header: s.modalHeader,
    body: s.modalBody,
  },
};

const form: NonNullable<AntConfigProviderProps['form']> = {
  className: s.form,
};

const pagination: NonNullable<AntConfigProviderProps['pagination']> = {
  className: s.pagination,
};

const tabs: NonNullable<AntConfigProviderProps['tabs']> = {
  className: s.tabs,
};

const upload: NonNullable<AntConfigProviderProps['upload']> = {
  className: s.upload,
};

const collapse: NonNullable<AntConfigProviderProps['collapse']> = {
  className: s.collapse,
};

const checkbox: NonNullable<AntConfigProviderProps['checkbox']> = {
  className: s.checkbox,
};

export const AntThemeProvider: FC<PropsWithChildren> = (props) => {
  const { i18n } = useTranslation();

  const antLocale = ANT_LOCALE_MAPPING[i18n.language];

  const theme = useMemo(() => {
    const result: NonNullable<AntConfigProviderProps['theme']> = {
      token: {
        borderRadius: getCSSNumberValue('--input-button-border-radius'),
        colorPrimary: getCSSVarValue('--x-color-action'),
        colorLinkHover: getCSSVarValue('--x-color-action-active'),
        colorSplit: getCSSVarValue('--x-color-border'),
        fontFamily: getCSSVarValue('--x_text_ff'),
        colorError: getCSSVarValue('--x-color-action-red'),
        screenXSMax: parseInt(screenVariables.mobileWidth, 10),
        screenSM: parseInt(screenVariables.tabletWidth, 10),
        screenSMMin: parseInt(screenVariables.tabletWidth, 10),
      },
      components: {
        Button: {
          defaultShadow: 'none',
          fontWeight: getCSSVarValue('--x_text_fw_semibold'),
          colorError: getCSSVarValue('--x-color-action-red'),
          defaultColor: getCSSVarValue('--x-color-text-accent'),
          defaultHoverColor: getCSSVarValue('--x-color-text-accent-active'),
        },
        Table: {
          headerBg: getCSSVarValue('--x-color-background'),
          borderColor: getCSSVarValue('--x-color-border'),
          cellFontSizeSM: 13,
        },
        Menu: {
          itemColor: getCSSVarValue('--x-color-text-primary'),
          colorHighlight: getCSSVarValue('--x_color_navy_10'),
          colorPrimary: getCSSVarValue('--x-color-border-black'),
          itemMarginInline: 0,
        },
        Form: {
          itemMarginBottom: 16,
        },
        DatePicker: {
          borderRadius: getCSSNumberValue('--input-button-border-radius'),
        },
        Dropdown: {
          zIndexPopup: getCSSNumberValue('--z-index-ant-dropdown'),
        },
        Collapse: {
          colorFillAlter: getCSSVarValue('--x-color-background'),
        },
        Tooltip: {
          zIndexPopupBase: getCSSNumberValue('--z-index-ant-tooltip'),
        },
        Modal: {
          zIndexPopupBase: getCSSNumberValue('--z-index-ant-modal'),
        },
        InputNumber: {
          borderRadius: getCSSNumberValue('--input-button-border-radius'),
        },
        Upload: {
          colorBorder: getCSSVarValue('--x-color-action'),
          colorFillAlter: getCSSVarValue('--x-color-background-accent-lighter'),
          borderRadiusLG: 4,
        },
        Pagination: {
          borderRadius: 4,
        },
        Card: {
          borderRadiusLG: 4,
        },
        Calendar: {
          colorSplit: getCSSVarValue('--x-color-border'),
        },
      },
    };

    return result;
  }, []);

  return (
    <ConfigProvider
      locale={antLocale}
      theme={theme}
      tooltip={tooltip}
      select={select}
      modal={modal}
      pagination={pagination}
      upload={upload}
      tabs={tabs}
      form={form}
      collapse={collapse}
      checkbox={checkbox}
    >
      <AntApp>{props.children}</AntApp>
    </ConfigProvider>
  );
};
